
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

export default function ServicesPage() {
  const coreServices = [
    {
      title: 'Lead Generation A.I.',
      description: 'Intelligent lead capture and nurturing system that works 24/7 to convert prospects into qualified appointments.',
      features: [
        'Automated lead scoring and qualification',
        'Multi-channel outreach campaigns',
        'Intelligent conversation flows',
        'CRM integration and data sync',
        'Real-time lead notifications'
      ],
      pricing: 'Starting at $497/month',
      image: 'https://public.readdy.ai/ai/img_res/927b584f1b67452804e884bf7a785262.jpg'
    },
    {
      title: 'Process Automation A.I.',
      description: 'Streamline your operations with intelligent automation that handles repetitive tasks and optimizes workflows.',
      features: [
        'Automated billing and invoicing',
        'Document processing and filing',
        'Appointment scheduling optimization',
        'Inventory and resource management',
        'Compliance monitoring and reporting'
      ],
      pricing: 'Starting at $397/month',
      image: 'https://readdy.ai/api/search-image?query=Business%20process%20automation%20dashboard%2C%20modern%20workflow%20management%20interface%2C%20automated%20operations%20center%2C%20professional%20office%20environment%20with%20digital%20systems%2C%20streamlined%20business%20processes%20visualization&width=600&height=400&seq=service-automation&orientation=landscape'
    },
    {
      title: 'Client Experience A.I.',
      description: 'Transform customer interactions with personalized A.I. that enhances satisfaction and drives retention.',
      features: [
        'Personalized customer journeys',
        'Automated follow-up sequences',
        'Sentiment analysis and feedback',
        'Loyalty program management',
        'Predictive customer insights'
      ],
      pricing: 'Starting at $597/month',
      image: 'https://readdy.ai/api/search-image?query=Customer%20experience%20management%20dashboard%2C%20modern%20CRM%20interface%20with%20AI%20personalization%2C%20professional%20client%20service%20environment%2C%20customer%20satisfaction%20analytics%2C%20automated%20engagement%20tools&width=600&height=400&seq=service-experience&orientation=landscape'
    }
  ];

  const addOnServices = [
    {
      title: 'A.I. Web Design',
      description: 'Complete website redesign with built-in automation and conversion optimization.',
      features: ['Conversion-optimized design', 'Chatbot integration', 'Lead capture forms', 'Mobile responsive'],
      pricing: '$2,997 one-time'
    },
    {
      title: 'A.I. Lead Generation Add-On',
      description: 'Advanced prospecting system that identifies and qualifies high-intent leads.',
      features: ['Advanced lead scoring', 'Automated prospecting', 'Qualification workflows', 'CRM integration'],
      pricing: '$297/month additional'
    }
  ];

  const industries = [
    {
      name: 'Health & Wellness',
      description: 'Patient scheduling, follow-ups, and retention automation for healthcare providers.',
      benefits: ['40% increase in patient retention', 'Automated appointment reminders', 'HIPAA-compliant systems']
    },
    {
      name: 'Home Services',
      description: 'Instant quotes, job booking, and customer communication automation.',
      benefits: ['60% faster quote delivery', '24/7 booking availability', 'Automated follow-ups']
    },
    {
      name: 'Finance',
      description: 'Client onboarding, compliance documentation, and communication automation.',
      benefits: ['75% reduction in onboarding time', 'Automated compliance checks', 'Secure document handling']
    },
    {
      name: 'Professional Services',
      description: 'HR automation, client management, and communication streamlining.',
      benefits: ['50% improvement in client satisfaction', 'Automated HR processes', 'Streamlined communications']
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-16">
        {/* Hero Section */}
        <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              A.I. Agent <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Deployment</span> Services
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Transform your business with intelligent automation that works around the clock to generate leads, streamline operations, and enhance customer experiences.
            </p>
            <Button size="lg" href="/contact">
              Get Your Custom A.I. Strategy
            </Button>
          </div>
        </section>

        {/* Core Services */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Core A.I. Services</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Choose from our comprehensive suite of A.I. solutions designed to automate and optimize every aspect of your business.
              </p>
            </div>

            <div className="space-y-16">
              {coreServices.map((service, index) => (
                <div key={index} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
                  <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                    <h3 className="text-3xl font-bold text-gray-900 mb-4">{service.title}</h3>
                    <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                    
                    <ul className="space-y-3 mb-6">
                      {service.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-center space-x-3">
                          <div className="w-5 h-5 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center">
                            <i className="ri-check-line text-white text-xs"></i>
                          </div>
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <div className="flex items-center justify-between">
                      <div className="text-2xl font-bold text-gray-900">{service.pricing}</div>
                      <Button href="/contact">Learn More</Button>
                    </div>
                  </div>
                  
                  <div className={index % 2 === 1 ? 'lg:col-start-1' : ''}>
                    <img 
                      src={service.image}
                      alt={service.title}
                      className="w-full h-80 object-cover rounded-2xl shadow-lg"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Add-On Services */}
        <section className="py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Add-On Services</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Enhance your A.I. deployment with specialized services for maximum impact.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {addOnServices.map((service, index) => (
                <div key={index} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                  <p className="text-gray-600 mb-6">{service.description}</p>
                  
                  <ul className="space-y-2 mb-6">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center space-x-2">
                        <i className="ri-check-line text-blue-600"></i>
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <div className="flex items-center justify-between">
                    <div className="text-xl font-bold text-gray-900">{service.pricing}</div>
                    <Button variant="outline" href="/contact">Add to Package</Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Industry Solutions */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Industry-Specific Solutions</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Tailored A.I. implementations designed for the unique needs of your industry.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {industries.map((industry, index) => (
                <div key={index} className="bg-gray-50 rounded-2xl p-8 hover:shadow-lg transition-shadow duration-300">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{industry.name}</h3>
                  <p className="text-gray-600 mb-6">{industry.description}</p>
                  
                  <div className="space-y-2">
                    {industry.benefits.map((benefit, benefitIndex) => (
                      <div key={benefitIndex} className="flex items-center space-x-2">
                        <i className="ri-arrow-right-line text-blue-600"></i>
                        <span className="text-gray-700 text-sm">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 bg-gradient-to-r from-gray-900 to-black">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Transform Your Business?
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Get a custom A.I. strategy session and discover how our solutions can automate your growth.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" href="/contact">
                Schedule Strategy Session
              </Button>
              <Button variant="outline" size="lg" href="/case-studies" className="border-gray-400 text-gray-300 hover:border-white hover:text-white">
                View Case Studies
              </Button>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
}
